/**
 * HTML Sanitization Utility
 * 
 * Uses DOMPurify to sanitize HTML content before rendering with dangerouslySetInnerHTML.
 * This prevents XSS attacks while allowing safe HTML formatting.
 * 
 * @module utils/sanitize
 */

import DOMPurify from 'dompurify';

/**
 * Configuration for HTML sanitization
 * Allows common formatting tags while blocking dangerous content
 */
const SANITIZE_CONFIG = {
  // Allowed HTML tags for rich text content
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 'b', 'i',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'span', 'div',
    'blockquote', 'pre', 'code',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'img'
  ],
  
  // Allowed attributes
  ALLOWED_ATTR: [
    'href',      // For links
    'class',     // For styling
    'style',     // For inline styles (can be restricted further if needed)
    'target',    // For link targets
    'rel',       // For link relationships
    'alt',       // For images
    'src',       // For images
    'title'      // For tooltips
  ],
  
  // Disallow data attributes to prevent XSS
  ALLOW_DATA_ATTR: false,
  
  // Additional security options
  KEEP_CONTENT: true,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false,
  
  // Sanitize style attributes
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * 
 * @param {string} dirty - Unsanitized HTML string
 * @param {Object} customConfig - Optional custom DOMPurify configuration
 * @returns {string} Sanitized HTML string safe for rendering
 * 
 * @example
 * import { sanitizeHTML } from '../utils/sanitize';
 * 
 * <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(userContent) }} />
 */
export const sanitizeHTML = (dirty, customConfig = {}) => {
  // Return empty string for null/undefined
  if (!dirty) {
    return '';
  }
  
  // Convert to string if not already
  const htmlString = String(dirty);
  
  // Merge custom config with default config
  const config = {
    ...SANITIZE_CONFIG,
    ...customConfig
  };
  
  // Sanitize and return
  return DOMPurify.sanitize(htmlString, config);
};

/**
 * Sanitizes HTML with stricter rules for user-generated content
 * Use this for content that comes directly from user input
 * 
 * @param {string} dirty - Unsanitized HTML string
 * @returns {string} Sanitized HTML string
 */
export const sanitizeUserContent = (dirty) => {
  const strictConfig = {
    ...SANITIZE_CONFIG,
    // Remove style attribute for user content
    ALLOWED_ATTR: SANITIZE_CONFIG.ALLOWED_ATTR.filter(attr => attr !== 'style'),
    // Remove potentially dangerous tags
    ALLOWED_TAGS: SANITIZE_CONFIG.ALLOWED_TAGS.filter(
      tag => !['img', 'a'].includes(tag)
    )
  };
  
  return sanitizeHTML(dirty, strictConfig);
};

/**
 * Sanitizes plain text (strips all HTML)
 * Use this when you only need plain text output
 * 
 * @param {string} dirty - HTML string to convert to plain text
 * @returns {string} Plain text string
 */
export const sanitizeToText = (dirty) => {
  if (!dirty) {
    return '';
  }
  
  const config = {
    ALLOWED_TAGS: [],
    KEEP_CONTENT: true
  };
  
  return DOMPurify.sanitize(String(dirty), config);
};

/**
 * Validates if content is safe to render
 * Returns true if content passes sanitization without changes
 * 
 * @param {string} content - HTML content to validate
 * @returns {boolean} True if content is safe
 */
export const isContentSafe = (content) => {
  if (!content) {
    return true;
  }
  
  const sanitized = sanitizeHTML(content);
  return sanitized === content;
};

